// frontend/src/components/reputation/GmbPostsTab.jsx
// Version: 2.0 (eln-gbp-posts-round1)
// Description: Экран «Вышло за сутки» и управление публикациями Google Business Profile.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';
import {
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  Edit3,
  ExternalLink,
  ShieldCheck,
  ShieldAlert,
  MapPin,
  Globe,
  Clock,
  Check,
  Copy,
  X
} from 'lucide-react';

/**
 * Returns the latest Katifi review record for the current rewrite (after the last rewrite_suggested).
 * Old Katifi verdict on the previous text is ignored.
 */
export const getRewriteKatifiReview = (post) => {
  if (!post || !Array.isArray(post.revision_history)) return null;
  const history = post.revision_history;
  let lastRewriteIdx = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]?.action === 'rewrite_suggested') {
      lastRewriteIdx = i;
      break;
    }
  }
  if (lastRewriteIdx === -1) {
    return history.slice().reverse().find((h) => h?.action === 'katifi_reviewed' && h?.is_rewrite) || null;
  }
  for (let i = history.length - 1; i > lastRewriteIdx; i--) {
    if (history[i]?.action === 'katifi_reviewed') {
      return history[i];
    }
  }
  return null;
};

/**
 * Returns the latest Katifi review failure record for the current rewrite.
 */
export const getRewriteKatifiFailed = (post) => {
  if (!post || !Array.isArray(post.revision_history)) return null;
  const history = post.revision_history;
  let lastRewriteIdx = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]?.action === 'rewrite_suggested') {
      lastRewriteIdx = i;
      break;
    }
  }
  if (lastRewriteIdx === -1) {
    return history.slice().reverse().find((h) => h?.action === 'katifi_review_failed' && h?.is_rewrite) || null;
  }
  for (let i = history.length - 1; i > lastRewriteIdx; i--) {
    if (history[i]?.action === 'katifi_review_failed') {
      return history[i];
    }
  }
  return null;
};

/**
 * Format datetime in America/New_York timezone.
 * Handles UTC strings without 'Z' suffix correctly.
 */
export const formatNyDateTime = (dtStr, lang = 'en') => {
  if (!dtStr) return '';
  const str = String(dtStr).trim();
  const iso = str.endsWith('Z') || str.includes('+') || /-\d{2}:\d{2}$/.test(str)
    ? str
    : (str.includes('T') ? str + 'Z' : str.replace(' ', 'T') + 'Z');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', { timeZone: 'America/New_York' });
};

/**
 * Format date in America/New_York timezone.
 */
export const formatNyDate = (dtStr, lang = 'en') => {
  if (!dtStr) return '';
  const str = String(dtStr).trim();
  const iso = str.endsWith('Z') || str.includes('+') || /-\d{2}:\d{2}$/.test(str)
    ? str
    : (str.includes('T') ? str + 'Z' : str.replace(' ', 'T') + 'Z');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', { timeZone: 'America/New_York' });
};

export const GmbPostsTab = ({ profileId: propProfileId, profiles = [], highlightPostId: propHighlightPostId }) => {
  const { lang: rawLang, t: contextT } = useUiLanguage();
  const lang = rawLang || 'ru';
  const t = useCallback((k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v)), [rawLang, contextT]);

  // Derive highlightPostId from prop or window location search params
  const highlightPostId = useMemo(() => {
    if (propHighlightPostId) return propHighlightPostId;
    try {
      if (typeof window !== 'undefined' && window.location && window.location.search) {
        return new URLSearchParams(window.location.search).get('postId');
      }
    } catch (_) {}
    return null;
  }, [propHighlightPostId]);

  // Derive profileId from prop or first available profile
  const profileId = useMemo(() => {
    if (propProfileId) return propProfileId;
    if (profiles && profiles.length > 0 && profiles[0].id) return profiles[0].id;
    return null;
  }, [propProfileId, profiles]);

  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState({ posts: [], unverified_count: 0 });
  const [stats7d, setStats7d] = useState({ published_count: 0, target: 3, target_exceeded: false, last_published_at: null });
  const [locations, setLocations] = useState([]);
  const [canonicalTopics, setCanonicalTopics] = useState([]);
  const [officesError, setOfficesError] = useState(null);
  const [topicsError, setTopicsError] = useState(null);
  const [filterPendingOnly, setFilterPendingOnly] = useState(false);
  const [filterManualOnly, setFilterManualOnly] = useState(false);
  const [filterUnverifiedOnly, setFilterUnverifiedOnly] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [postsApiEnabled, setPostsApiEnabled] = useState(false);
  const [geminiConfigured, setGeminiConfigured] = useState(false);

  // Manual publication modal/state
  const [manualModalPost, setManualModalPost] = useState(null);
  const [manualGoogleUrl, setManualGoogleUrl] = useState('');
  const [markingManual, setMarkingManual] = useState(false);

  // Owner override approval modal/state (when Katifi failed or has remarks)
  const [overrideModalPost, setOverrideModalPost] = useState(null);
  const [overrideComment, setOverrideComment] = useState('');
  const [approvingOverride, setApprovingOverride] = useState(false);

  // Stop flag & autopublish pipeline state (Task 30.10.2026)
  const [autopublishStop, setAutopublishStop] = useState(null);
  const [nextAutopublish, setNextAutopublish] = useState(null);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeComment, setResumeComment] = useState('');
  const [resumingAutopublish, setResumingAutopublish] = useState(false);

  // Publication Unconfirmed resolution state (Round 2, B1)
  const [confirmPubPost, setConfirmPubPost] = useState(null);
  const [confirmPubUrl, setConfirmPubUrl] = useState('');
  const [confirmPubComment, setConfirmPubComment] = useState('');
  const [confirmingPub, setConfirmingPub] = useState(false);

  const [revertAppPost, setRevertAppPost] = useState(null);
  const [revertAppComment, setRevertAppComment] = useState('');
  const [revertingApp, setRevertingApp] = useState(false);

  // Google Policy Removed state (Task 30.10.2026, B1)
  const [googlePolicyPost, setGooglePolicyPost] = useState(null);
  const [googlePolicyNotice, setGooglePolicyNotice] = useState('');
  const [googlePolicyComment, setGooglePolicyComment] = useState('');
  const [submittingGooglePolicy, setSubmittingGooglePolicy] = useState(false);

  // Google Posting Restored state (Task 30.10.2026, B1)
  const [restoreGoogleModalOpen, setRestoreGoogleModalOpen] = useState(false);
  const [restoreGoogleComment, setRestoreGoogleComment] = useState('');
  const [restoringGooglePosting, setRestoringGooglePosting] = useState(false);

  // Form state for creating a new post
  const [targetOffice, setTargetOffice] = useState('');
  const [postLanguage, setPostLanguage] = useState('en');
  const [selectedTopicObj, setSelectedTopicObj] = useState(null);
  const [postSummary, setPostSummary] = useState('');
  const [postCtaType, setPostCtaType] = useState('LEARN_MORE');
  const [postCtaUrl, setPostCtaUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Patching modal/state
  const [editingPost, setEditingPost] = useState(null);
  const [editSummary, setEditSummary] = useState('');
  const [patching, setPatching] = useState(false);

  // Rejecting modal/state
  const [rejectingPost, setRejectingPost] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Rewriting modal/state (A2)
  const [rewritingPost, setRewritingPost] = useState(null);
  const [rewriteFeedback, setRewriteFeedback] = useState('');
  const [rewriting, setRewriting] = useState(false);

  // Loading state for single action buttons
  const [actionLoading, setActionLoading] = useState({});

  const setItemLoading = (key, val) => {
    setActionLoading((prev) => ({ ...prev, [key]: val }));
  };

  const fetchDailyPosts = useCallback(async (officeKeyOverride) => {
    setLoading(true);
    try {
      const activeOffice = officeKeyOverride || targetOffice || 'manhattan';
      const res = await axios.get(`/api/v1/reputation/gmb/posts?office_key=${encodeURIComponent(activeOffice)}`);
      if (res?.data) {
        setDailyData({
          posts: Array.isArray(res.data.posts) ? res.data.posts : [],
          unverified_count: Number(res.data.unverified_count) || 0,
        });
        setAutopublishStop(res.data.autopublish_stop || res.data.stats_7d?.autopublish_stop || null);
        setNextAutopublish(res.data.next_autopublish || res.data.stats_7d?.next_autopublish || null);
        if (res.data.stats_7d) {
          setStats7d({
            published_count: Number(res.data.stats_7d.published_count) || 0,
            target: Number(res.data.stats_7d.target) || 3,
            target_exceeded: Boolean(res.data.stats_7d.target_exceeded),
            last_published_at: res.data.stats_7d.last_published_at || null,
          });
        }
      }
    } catch (err) {
      console.warn('Failed to load daily posts:', err);
    } finally {
      setLoading(false);
    }
  }, [targetOffice]);

  const fetchLocationsAndTopics = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        axios.get('/api/v1/reputation/gmb/offices'),
        axios.get('/api/v1/reputation/gmb/topics'),
      ]);

      const [officesRes, topicsRes] = results;

      if (officesRes.status === 'fulfilled' && (officesRes.value.data?.offices || officesRes.value.data?.locations)) {
        const offList = officesRes.value.data.offices || officesRes.value.data.locations || [];
        setLocations(offList);
        setOfficesError(null);
        if (offList.length > 0 && !targetOffice) {
          const firstKey = offList[0].office_key || offList[0].id || 'manhattan';
          setTargetOffice(firstKey);
          fetchDailyPosts(firstKey);
        }
      } else {
        setLocations([]);
        const errData = officesRes.reason?.response?.data?.detail;
        const msg = typeof errData === 'object' && errData?.message ? errData.message : (errData || officesRes.reason?.message || 'Failed to load offices');
        setOfficesError(msg);
      }

      if (topicsRes.status === 'fulfilled') {
        const topList = topicsRes.value.data?.topics || [];
        setCanonicalTopics(topList);
        setTopicsError(null);
        if (topList.length > 0 && !selectedTopicObj) {
          const first = topList[0];
          setSelectedTopicObj(first);
          setPostCtaUrl(postLanguage === 'ru' ? (first.url_ru || first.url_en) : first.url_en);
        }
      } else {
        setCanonicalTopics([]);
        const errData = topicsRes.reason?.response?.data?.detail;
        const msg = typeof errData === 'object' && errData?.message ? errData.message : (errData || topicsRes.reason?.message || 'Failed to load topics');
        setTopicsError(msg);
      }
    } catch (err) {
      console.warn('Failed to load GMB auxiliary data:', err);
    }
  }, [profileId, postLanguage, selectedTopicObj, targetOffice]);

  const fetchAuthStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/reputation/gmb/auth/status');
      setIsGoogleConnected(Boolean(res.data?.can_sync));
      setPostsApiEnabled(Boolean(res.data?.posts_api_enabled));
      setGeminiConfigured(Boolean(res.data?.gemini_configured));
    } catch (_) {
      setIsGoogleConnected(false);
      setPostsApiEnabled(false);
      setGeminiConfigured(false);
    }
  }, []);

  const handleCopyText = useCallback((textToCopy) => {
    const text = typeof textToCopy === 'string' ? textToCopy : postSummary;
    if (!text || !text.trim()) return;
    navigator.clipboard?.writeText(text);
    toast.success(t('rep.gmb.copy_text_success') || 'Text copied to clipboard');
  }, [postSummary, t]);

  useEffect(() => {
    if (profileId) {
      fetchDailyPosts();
      fetchLocationsAndTopics();
      fetchAuthStatus();
    }
  }, [profileId, fetchDailyPosts, fetchLocationsAndTopics, fetchAuthStatus]);

  useEffect(() => {
    if (highlightPostId && typeof document !== 'undefined') {
      const el = document.getElementById(`post-${highlightPostId}`) || document.getElementById(`gmb-post-${highlightPostId}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightPostId, dailyData.posts]);

  // When language or topic changes, update CTA url to match canonical page
  const handleTopicChange = (topicSlug) => {
    const found = canonicalTopics.find((t) => (t.slug || t.id || t.name) === topicSlug);
    if (found) {
      setSelectedTopicObj(found);
      setPostCtaUrl(postLanguage === 'ru' ? (found.url_ru || found.url_en) : found.url_en);
    }
  };

  const handleLanguageToggle = (newLang) => {
    setPostLanguage(newLang);
    if (selectedTopicObj) {
      setPostCtaUrl(newLang === 'ru' ? selectedTopicObj.url_ru : selectedTopicObj.url_en);
    }
  };

  // Generate draft with AI
  const handleGenerateAI = async () => {
    if (!geminiConfigured) {
      toast.error(t('rep.gmb.gemini_key_missing') || 'GEMINI_API_KEY is not configured');
      return;
    }
    if (!targetOffice) {
      toast.error(t('rep.modal.gmb.toast.select_office') || 'Выберите офис перед генерацией поста');
      return;
    }
    setGenerating(true);
    try {
      const topicName = selectedTopicObj ? (postLanguage === 'ru' ? selectedTopicObj.title_ru : selectedTopicObj.title_en) : '';
      const res = await axios.post(`/api/v1/reputation/gmb/posts/generate?office_key=${encodeURIComponent(targetOffice)}`, {
        topic: topicName,
        language: postLanguage,
      });
      if (res.data?.summary) {
        setPostSummary(res.data.summary);
        if (res.data.cta_url) setPostCtaUrl(res.data.cta_url);
        toast.success(t('rep.modal.gmb.toast.draft_generated'));
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'object' && d?.message ? d.message : (d || err.message);
      toast.error(t('rep.modal.gmb.toast.draft_failed', { reason: msg }));
    } finally {
      setGenerating(false);
    }
  };

  // Publish / Create Post
  const handleCreatePost = async () => {
    if (!postSummary.trim()) {
      toast.error(t('rep.modal.gmb.toast.post_empty'));
      return;
    }
    if (!targetOffice) {
      toast.error(t('rep.modal.gmb.toast.select_office') || 'Выберите офис перед публикацией');
      return;
    }

    if (stats7d.target_exceeded) {
      const proceed = window.confirm(
        t('rep.gmb.rhythm_warning_msg', {
          count: stats7d.published_count,
          target: stats7d.target,
        })
      );
      if (!proceed) return;
    }

    setSubmitting(true);
    try {
      const topicTitle = selectedTopicObj ? (postLanguage === 'ru' ? selectedTopicObj.title_ru : selectedTopicObj.title_en) : '';
      const res = await axios.post('/api/v1/reputation/gmb/posts', {
        summary: postSummary,
        cta_type: postCtaType,
        cta_url: postCtaUrl,
        post_language: postLanguage,
        office_key: targetOffice,
        ai_topic: topicTitle,
      });
      if (res.data?.live_published) {
        toast.success(t('rep.modal.gmb.toast.post_live'));
      } else {
        toast.success(t('rep.gmb.toast_draft_saved') || 'Черновик сохранён в OS и ожидает проверки Katifi');
      }
      setPostSummary('');
      fetchDailyPosts();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'object' && d?.message ? d.message : (d || err.message);
      toast.error(t('rep.modal.gmb.toast.post_failed', { reason: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  // Verify post (forwards to Katifi compliance review)
  const handleVerify = async (postId) => {
    return handleVerifyWithKatifi(postId);
  };

  // Verify with Katifi (AI reasoner compliance review)
  const handleVerifyWithKatifi = async (postId) => {
    setItemLoading(`verify_katifi_${postId}`, true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${postId}/verify`);
      if (res.data?.verdict === 'pass') {
        toast.success(t('rep.gmb.katifi_badge_pass') || 'Katifi verification passed');
      } else if (res.data?.verdict === 'fail') {
        toast.error((res.data.notes || []).join('; ') || 'Katifi identified compliance remarks');
      } else if (res.data?.status === 'katifi_not_reviewed') {
        toast.error((t('rep.gmb.katifi_badge_not_reviewed') || 'Katifi not reviewed') + ': ' + (res.data.error || 'unavailable'));
      }
      fetchDailyPosts();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setItemLoading(`verify_katifi_${postId}`, false);
    }
  };

  // Approve post from pending_approval, needs_review, katifi_reviewed, or pending_rewrite
  const handleApprove = async (postId, post) => {
    const isRewrite = post?.status === 'pending_rewrite';
    const rewriteKatifi = isRewrite ? getRewriteKatifiReview(post) : null;
    const katifiEntry = isRewrite
      ? rewriteKatifi
      : (post?.revision_history || []).slice().reverse().find((h) => h.action === 'katifi_reviewed');
    const isFail = isRewrite
      ? rewriteKatifi?.verdict === 'fail'
      : (katifiEntry?.verdict === 'fail' || post?.status === 'needs_review');

    if (isFail) {
      setOverrideModalPost(post);
      setOverrideComment('');
      return;
    }

    setItemLoading(`approve_${postId}`, true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${postId}/approve`);
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_approved') || 'Пост успешно утверждён');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setItemLoading(`approve_${postId}`, false);
    }
  };

  // Confirm owner override of Katifi remarks
  const handleConfirmOverride = async () => {
    if (!overrideModalPost) return;
    if (!overrideComment.trim()) {
      toast.error(t('rep.gmb.katifi_override_comment_required') || 'Комментарий владельца обязателен');
      return;
    }
    setApprovingOverride(true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${overrideModalPost.id}/approve`, {
        override_katifi: true,
        override_comment: overrideComment.trim(),
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_approved') || 'Пост утверждён с комментарием владельца');
        setOverrideModalPost(null);
        setOverrideComment('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setApprovingOverride(false);
    }
  };

  const handleResumeAutopublish = async () => {
    if (!resumeComment.trim()) {
      toast.error(t('rep.gmb.resume_comment_required') || 'Обоснование владельца обязательно');
      return;
    }
    setResumingAutopublish(true);
    try {
      const res = await axios.post('/api/v1/reputation/gmb/bridge/stop-flag/resume', {
        office_key: targetOffice || 'manhattan',
        comment: resumeComment.trim(),
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_resumed') || 'Конвейер автопубликации возобновлен');
        setResumeModalOpen(false);
        setResumeComment('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setResumingAutopublish(false);
    }
  };

  // Open reject modal
  const handleOpenReject = (post) => {
    setRejectingPost(post);
    setRejectReason('');
  };

  // Submit rejection
  const handleReject = async () => {
    if (!rejectingPost) return;
    setRejecting(true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${rejectingPost.id}/reject`, {
        reason: rejectReason.trim() || undefined,
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_rejected') || 'Черновик отклонён');
        setRejectingPost(null);
        setRejectReason('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setRejecting(false);
    }
  };

  // Delete post from Google or OS
  const handleDelete = async (postOrId) => {
    const post = typeof postOrId === 'object' && postOrId !== null
      ? postOrId
      : (dailyData.posts || []).find((p) => String(p.id) === String(postOrId));
    const postId = post?.id || postOrId;
    const isManual = post?.status === 'published_manually';
    const confirmMsg = isManual
      ? (t('rep.gmb.confirm_delete_manual') || 'Пост опубликован вручную — удалите его в Google сами; OS изменит только свою запись. Продолжить?')
      : t('rep.gmb.confirm_delete');
    if (!window.confirm(confirmMsg)) return;
    setItemLoading(`delete_${postId}`, true);
    try {
      const res = await axios.delete(`/api/v1/reputation/gmb/posts/${postId}`);
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_deleted'));
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setItemLoading(`delete_${postId}`, false);
    }
  };

  // Open patch modal
  const handleOpenPatch = (post) => {
    setEditingPost(post);
    setEditSummary(post.summary || '');
  };

  // Submit patch to Google
  const handleSavePatch = async () => {
    if (!editingPost) return;
    setPatching(true);
    try {
      const res = await axios.patch(`/api/v1/reputation/gmb/posts/${editingPost.id}`, {
        summary: editSummary,
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_patched'));
        setEditingPost(null);
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setPatching(false);
    }
  };

  // Open rewrite modal (A2)
  const handleOpenRewrite = (post) => {
    setRewritingPost(post);
    setRewriteFeedback('');
  };

  // Submit rewrite request to AI & update Google
  const handleRewrite = async () => {
    if (!rewritingPost) return;
    setRewriting(true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${rewritingPost.id}/rewrite`, {
        feedback: rewriteFeedback.trim() || undefined
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_rewritten'));
        setRewritingPost(null);
        setRewriteFeedback('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setRewriting(false);
    }
  };


  const handleCopyLink = (url) => {
    if (!url) return;
    try {
      navigator.clipboard.writeText(url);
      toast.success(t('rep.gmb.copied_link_success') || 'Ссылка кнопки скопирована в буфер');
    } catch (_) {
      toast.error('Не удалось скопировать в буфер');
    }
  };

  const handleOpenManualModal = (post) => {
    setManualModalPost(post);
    setManualGoogleUrl('');
  };

  const handleConfirmManual = async () => {
    if (!manualModalPost) return;
    if (!manualGoogleUrl || !manualGoogleUrl.trim()) {
      toast.error(t('rep.gmb.manual_url_required') || 'Ссылка на пост в Google обязательна (posts.gle, maps.app.goo.gl, google.com/maps)');
      return;
    }
    setMarkingManual(true);
    try {
      const res = await axios.post(
        `/api/v1/reputation/gmb/posts/${manualModalPost.id}/mark-published-manually`,
        { google_post_url: manualGoogleUrl.trim() }
      );
      if (res.data?.success) {
        toast.success(t('rep.gmb.manual_toast_success') || 'Пост отмечен как опубликованный вручную');
        setManualModalPost(null);
        setManualGoogleUrl('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setMarkingManual(false);
    }
  };

  const handleOpenConfirmPubModal = (post) => {
    setConfirmPubPost(post);
    setConfirmPubUrl('');
    setConfirmPubComment('');
  };

  const handleConfirmPublished = async () => {
    if (!confirmPubPost) return;
    if (!confirmPubUrl || !confirmPubUrl.trim()) {
      toast.error(t('rep.gmb.manual_url_required') || 'Google post URL is required');
      return;
    }
    if (!confirmPubComment || !confirmPubComment.trim()) {
      toast.error(t('rep.gmb.resume_comment_required') || 'Comment is mandatory');
      return;
    }
    setConfirmingPub(true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${confirmPubPost.id}/confirm-published`, {
        google_url: confirmPubUrl.trim(),
        comment: confirmPubComment.trim(),
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_confirm_published') || 'Post confirmed as published');
        setConfirmPubPost(null);
        setConfirmPubUrl('');
        setConfirmPubComment('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setConfirmingPub(false);
    }
  };

  const handleOpenRevertAppModal = (post) => {
    setRevertAppPost(post);
    setRevertAppComment('');
  };

  const handleRevertToApproved = async () => {
    if (!revertAppPost) return;
    if (!revertAppComment || !revertAppComment.trim()) {
      toast.error(t('rep.gmb.resume_comment_required') || 'Comment is mandatory');
      return;
    }
    setRevertingApp(true);
    try {
      const endpoint = revertAppPost.status === 'rejected'
        ? `/api/v1/reputation/gmb/posts/${revertAppPost.id}/clear-claim`
        : `/api/v1/reputation/gmb/posts/${revertAppPost.id}/revert-to-approved`;
      const res = await axios.post(endpoint, {
        comment: revertAppComment.trim(),
      });
      if (res.data?.success) {
        toast.success(
          revertAppPost.status === 'rejected'
            ? (t('rep.gmb.toast_claim_cleared') || 'Bridge claim cleared')
            : (t('rep.gmb.toast_revert_approved') || 'Post returned to approved status')
        );
        setRevertAppPost(null);
        setRevertAppComment('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setRevertingApp(false);
    }
  };

  const handleOpenGooglePolicyModal = (post) => {
    setGooglePolicyPost(post);
    setGooglePolicyNotice('');
    setGooglePolicyComment('');
  };

  const handleConfirmGooglePolicyRemoved = async () => {
    if (!googlePolicyPost) return;
    if (!googlePolicyNotice || !googlePolicyNotice.trim()) {
      toast.error(t('rep.gmb.google_notice_label') || 'Google notice text is mandatory');
      return;
    }
    if (!googlePolicyComment || !googlePolicyComment.trim()) {
      toast.error(t('rep.gmb.google_policy_comment_label') || 'Comment is mandatory');
      return;
    }
    setSubmittingGooglePolicy(true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/posts/${googlePolicyPost.id}/google-policy-removed`, {
        google_notice: googlePolicyNotice.trim(),
        comment: googlePolicyComment.trim(),
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_google_policy_removed') || 'Post marked as removed by Google. Pipeline stopped.');
        setGooglePolicyPost(null);
        setGooglePolicyNotice('');
        setGooglePolicyComment('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setSubmittingGooglePolicy(false);
    }
  };

  const handleRestoreGooglePosting = async () => {
    if (!restoreGoogleComment || !restoreGoogleComment.trim()) {
      toast.error(t('rep.gmb.restore_google_comment_label') || 'Comment is mandatory');
      return;
    }
    setRestoringGooglePosting(true);
    try {
      const res = await axios.post('/api/v1/reputation/gmb/bridge/stop-flag/google-posting-restored', {
        office_key: targetOffice || 'manhattan',
        comment: restoreGoogleComment.trim(),
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_google_posting_restored') || 'Google posting restored. Auto-publish pipeline resumed.');
        setRestoreGoogleModalOpen(false);
        setRestoreGoogleComment('');
        fetchDailyPosts();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setRestoringGooglePosting(false);
    }
  };

  const pendingApprovalCount = useMemo(() => {
    return (dailyData.posts || []).filter((p) => p.status === 'pending_approval' || p.status === 'pending_rewrite').length;
  }, [dailyData.posts]);

  const manualCount = useMemo(() => {
    return (dailyData.posts || []).filter((p) => p.status === 'published_manually').length;
  }, [dailyData.posts]);

  const displayedPosts = useMemo(() => {
    if (!dailyData.posts) return [];
    if (filterPendingOnly) {
      return dailyData.posts.filter((p) => p.status === 'pending_approval' || p.status === 'pending_rewrite');
    }
    if (filterManualOnly) {
      return dailyData.posts.filter((p) => p.status === 'published_manually');
    }
    if (filterUnverifiedOnly) {
      return dailyData.posts.filter((p) => !p.verified_at);
    }
    return dailyData.posts;
  }, [dailyData.posts, filterPendingOnly, filterManualOnly, filterUnverifiedOnly]);

  return (
    <div className="space-y-6">
      {/* ── STOP-FLAG BANNER (A4 / B1) ──────────────────────────────────────── */}
      {autopublishStop && (
        <div className="p-4 rounded-2xl bg-[#FDEDEC] border border-[#F5B7B1] text-[#922B21] flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
          <div className="flex items-start sm:items-center gap-2.5">
            <AlertTriangle className="w-5 h-5 text-[#C0392B] shrink-0 mt-0.5 sm:mt-0" />
            <div>
              <p className="text-xs font-bold text-[#922B21]">
                {autopublishStop.reason === 'google_posting_disabled'
                  ? t('rep.gmb.google_posting_disabled_banner', {
                      date: formatNyDate(autopublishStop.stopped_at, lang)
                    })
                  : t('rep.gmb.autopublish_stopped_banner', {
                      reason: (autopublishStop.reason === 'google_rejected'
                        ? (t('rep.gmb.reason_google_rejected') || 'Google rejected post')
                        : (autopublishStop.reason === 'three_consecutive_failures'
                          ? (t('rep.gmb.reason_three_consecutive_failures') || '3 consecutive bridge failures')
                          : (autopublishStop.reason === 'unresolved_claim'
                            ? (t('rep.gmb.reason_unresolved_claim') || 'Unresolved bridge claim exists')
                            : (autopublishStop.reason === 'publish_unconfirmed'
                              ? (t('rep.gmb.reason_publish_unconfirmed') || 'Publication unconfirmed after submit')
                              : autopublishStop.reason)))),
                      date: formatNyDate(autopublishStop.stopped_at, lang)
                    })}
              </p>
              {autopublishStop.reason === 'unresolved_claim' && (
                <button
                  type="button"
                  onClick={() => {
                    const targetId = autopublishStop.details?.post_id;
                    const el = targetId ? document.getElementById(`post-${targetId}`) : null;
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                      const firstUnres = document.querySelector('[data-unresolved-claim="true"]');
                      if (firstUnres) firstUnres.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#C0392B] underline hover:text-[#922B21] mt-1 cursor-pointer"
                >
                  {t('rep.gmb.btn_scroll_to_post') || 'Go to post'} →
                </button>
              )}
              {autopublishStop.details?.google_notice && (
                <p className="text-[11px] text-[#A93226] mt-0.5 italic">
                  «{autopublishStop.details.google_notice}»
                </p>
              )}
              {autopublishStop.details?.observed_status_text && (
                <p className="text-[11px] text-[#A93226] mt-0.5 italic">
                  «{autopublishStop.details.observed_status_text}»
                </p>
              )}
            </div>
          </div>
          {autopublishStop.reason === 'google_posting_disabled' ? (
            <button
              type="button"
              data-testid="restore-google-posting-btn"
              onClick={() => {
                setRestoreGoogleComment('');
                setRestoreGoogleModalOpen(true);
              }}
              className="h-8 px-4 rounded-xl text-xs font-bold bg-[#C0392B] text-white hover:bg-[#A93226] transition-colors cursor-pointer shrink-0 shadow-xs"
            >
              {t('rep.gmb.btn_restore_google_posting') || 'Google Restored Posting'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setResumeComment('');
                setResumeModalOpen(true);
              }}
              className="h-8 px-4 rounded-xl text-xs font-bold bg-[#C0392B] text-white hover:bg-[#A93226] transition-colors cursor-pointer shrink-0 shadow-xs"
            >
              {t('rep.gmb.btn_resume_autopublish') || 'Resume auto-publish'}
            </button>
          )}
        </div>
      )}

      {/* ── HEADER: «ВЫШЛО ЗА СУТКИ» ────────────────────────────────────────── */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-serif font-bold text-[#20231F] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#C8A653]" />
                <span>{t('rep.gmb.daily_title')}</span>
              </h2>
              {dailyData.unverified_count > 0 ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#C8A653]" />
                  <span>{t('rep.gmb.unverified_count', { count: dailyData.unverified_count })}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#0B8B5E]" />
                  <span>{t('rep.gmb.all_verified')}</span>
                </span>
              )}
            </div>
            <p className="text-xs text-[#687077] mt-1 leading-relaxed">
              {t('rep.gmb.daily_subtitle')}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-[#687077]">
              <span className="font-semibold text-[#20231F]">
                {t('rep.gmb.posts_per_week_counter', { count: stats7d.published_count, target: stats7d.target })}
              </span>
              {stats7d.target_exceeded && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFF4E5] text-[#B76E00] border border-[#FFE0B2]">
                  {t('rep.gmb.rhythm_warning_title') || 'Pace warning'}
                </span>
              )}
              <span>·</span>
              <span>
                {stats7d.last_published_at
                  ? t('rep.gmb.last_published_at', { date: formatNyDate(stats7d.last_published_at, lang) })
                  : t('rep.gmb.never_published')}
              </span>
              <span>·</span>
              <span>
                {nextAutopublish?.reason === 'stopped' && (
                  <span className="text-[#C0392B] font-semibold">{t('rep.gmb.next_autopublish_stopped') || 'auto-publish stopped'}</span>
                )}
                {nextAutopublish?.reason === 'target_reached' && (
                  <span className="text-[#B76E00]">{t('rep.gmb.next_autopublish_target_reached') || 'weekly limit reached'}</span>
                )}
                {nextAutopublish?.reason === 'interval_not_elapsed' && nextAutopublish?.eligible_at && (
                  <span>{t('rep.gmb.next_autopublish_at', { date: formatNyDateTime(nextAutopublish.eligible_at, lang) })}</span>
                )}
                {nextAutopublish?.reason === 'ready' && (
                  <span className="text-[#0B8B5E] font-medium">{t('rep.gmb.next_autopublish_ready') || 'ready for auto-publish'}</span>
                )}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setFilterPendingOnly(!filterPendingOnly);
                if (!filterPendingOnly) {
                  setFilterManualOnly(false);
                  setFilterUnverifiedOnly(false);
                }
              }}
              className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors cursor-pointer inline-flex items-center gap-1.5 ${
                filterPendingOnly
                  ? 'bg-[#20231F] text-white border-[#20231F]'
                  : 'bg-white text-[#20231F] border-[#E5E1D6] hover:bg-[#F9F7F1]'
              }`}
            >
              <span>{t('rep.gmb.filter_pending_approval') || 'Ждут утверждения'}</span>
              {pendingApprovalCount > 0 && (
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  filterPendingOnly
                    ? 'bg-[#C8A653] text-[#20231F]'
                    : 'bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]'
                }`}>
                  {pendingApprovalCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setFilterManualOnly(!filterManualOnly);
                if (!filterManualOnly) {
                  setFilterPendingOnly(false);
                  setFilterUnverifiedOnly(false);
                }
              }}
              className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors cursor-pointer inline-flex items-center gap-1.5 ${
                filterManualOnly
                  ? 'bg-[#20231F] text-white border-[#20231F]'
                  : 'bg-white text-[#20231F] border-[#E5E1D6] hover:bg-[#F9F7F1]'
              }`}
            >
              <span>{t('rep.gmb.filter_manual') || 'Опубликованы вручную'}</span>
              {manualCount > 0 && (
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  filterManualOnly
                    ? 'bg-[#2980B9] text-white'
                    : 'bg-[#EBF5FB] text-[#2980B9] border border-[#AED6F1]'
                }`}>
                  {manualCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setFilterUnverifiedOnly(!filterUnverifiedOnly);
                if (!filterUnverifiedOnly) {
                  setFilterPendingOnly(false);
                  setFilterManualOnly(false);
                }
              }}
              className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${
                filterUnverifiedOnly
                  ? 'bg-[#20231F] text-white border-[#20231F]'
                  : 'bg-white text-[#20231F] border-[#E5E1D6] hover:bg-[#F9F7F1]'
              }`}
            >
              {filterUnverifiedOnly ? t('rep.gmb.filter_unverified') : t('rep.gmb.filter_all')}
            </button>
            <button
              type="button"
              onClick={fetchDailyPosts}
              disabled={loading}
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] inline-flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-[#687077] ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? t('rep.state.loading') : t('rep.modal.gmb.btn_sync_now')}</span>
            </button>
          </div>
        </div>

        {/* КАРТОЧКА ОФИСА MANHATTAN */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-5 border-t border-[#F1EEE4]">
          {locations.map((loc) => {
            const isManhattan = loc.office_key === 'manhattan';
            return (
              <div
                key={loc.id}
                className="p-4 rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] flex items-center justify-between gap-3 shadow-2xs"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FAF4E5] border border-[#E5D7B5] flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-[#C8A653]" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#20231F] flex items-center gap-1.5">
                      <span>{isManhattan ? (t('rep.gmb.card_manhattan') || 'Manhattan Office') : (loc.title || loc.office_key)}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-white border border-[#E5E1D6] text-[#687077]">
                        {loc.office_key}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#687077] mt-0.5">
                      ⭐ {loc.rating ? loc.rating.toFixed(1) : '—'} · {loc.reviews_count || 0} {t('rep.modal.gmb.metric.reviews')}
                    </div>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-1">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      loc.sync_status === 'synced'
                        ? 'bg-[#E6F6EE] text-[#0B8B5E]'
                        : 'bg-[#FFF4E5] text-[#B76E00]'
                    }`}
                  >
                    {loc.sync_status === 'synced' ? '✓ Synced' : loc.sync_status || 'Pending'}
                  </span>
                  {loc.maps_uri && (
                    <a
                      href={loc.maps_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-[#C8A653] hover:underline inline-flex items-center gap-1 font-medium"
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                      <span>{t('rep.gmb.open_in_google_maps') || 'Open in Google'}</span>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── СПИСОК ПУБЛИКАЦИЙ ЗА СУТКИ ────────────────────────────────────────── */}
      <div className="space-y-4">
        {displayedPosts.length === 0 ? (
          <div className="bg-white border border-[#E5E1D6] rounded-2xl p-8 text-center text-xs text-[#687077]">
            {t('rep.gmb.empty_daily')}
          </div>
        ) : (
          displayedPosts.map((post) => {
            const isLive = post.status === 'published' || post.status === 'published_manually';
            const isDirectLive = post.status === 'published';
            const isManualLive = post.status === 'published_manually';
            const isNeedsReview = post.status === 'needs_review';
            const isFailed = post.status === 'failed';
            const isVerified = Boolean(post.verified_at);
            const isHighlighted = Boolean(highlightPostId && String(post.id) === String(highlightPostId));
            const rewriteKatifi = post.status === 'pending_rewrite' ? getRewriteKatifiReview(post) : null;
            const rewriteKatifiFailed = post.status === 'pending_rewrite' ? getRewriteKatifiFailed(post) : null;

            return (
              <div
                key={post.id}
                id={`post-${post.id}`}
                data-post-id={post.id}
                data-unresolved-claim={post.unresolved_claim ? "true" : undefined}
                data-highlighted={isHighlighted ? "true" : undefined}
                className={`bg-white border rounded-2xl p-5 shadow-xs space-y-3.5 transition-all ${
                  isHighlighted
                    ? 'border-[#C8A653] ring-2 ring-[#C8A653]/40 bg-[#FCFBF8]'
                    : 'border-[#E5E1D6] hover:border-[#D3CEBF]'
                }`}
              >
                {/* Header row: office, date, badges */}
                <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-[#F1EEE4] text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-[#20231F] flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-[#C8A653]" />
                      <span>{post.office_key || '—'}</span>
                    </span>
                    <span className="text-[#687077]">·</span>
                    <span className="text-[#687077]">
                      {formatNyDateTime(post.created_at, lang)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FCFBF8] border border-[#E5E1D6] text-[#20231F] uppercase">
                      <Globe className="w-2.5 h-2.5 text-[#C8A653]" />
                      <span>{post.post_language || 'EN'}</span>
                    </span>
                    {post.ai_topic && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E]">
                        {post.ai_topic}
                      </span>
                    )}
                  </div>

                  {/* Status Badges */}
                  <div className="flex items-center gap-1.5">
                    {/* Guard result */}
                    {post.guard_passed === false ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDF2F2] text-[#C93B2B] border border-[#FAD2CF]">
                        <ShieldAlert className="w-3 h-3 text-[#C93B2B]" />
                        <span>{t('rep.gmb.guard_violations_badge', { count: (post.guard_violations || []).length })}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <ShieldCheck className="w-3 h-3 text-[#0B8B5E]" />
                        <span>{t('rep.gmb.guard_passed_badge')}</span>
                      </span>
                    )}

                    {/* Honest status badge */}
                    {post.status === 'approved' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <CheckCircle2 className="w-3 h-3 text-[#0B8B5E]" />
                        <span>{t('rep.gmb.status.approved') || 'Approved'}</span>
                      </span>
                    )}
                    {post.status === 'katifi_reviewed' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <CheckCircle2 className="w-3 h-3 text-[#0B8B5E]" />
                        <span>{t('rep.gmb.katifi_badge_pass') || 'Katifi: PASSED'}</span>
                      </span>
                    )}
                    {post.status === 'katifi_not_reviewed' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <AlertTriangle className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.katifi_badge_not_reviewed') || 'Katifi: Not Reviewed'}</span>
                      </span>
                    )}
                    {isDirectLive && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>{t('rep.gmb.status.published')}</span>
                      </span>
                    )}
                    {isManualLive && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#EBF5FB] text-[#2980B9] border border-[#AED6F1]">
                        <CheckCircle2 className="w-3 h-3 text-[#2980B9]" />
                        <span>{t('rep.gmb.status.published_manually') || 'Опубликован вручную'}</span>
                      </span>
                    )}
                    {post.status === 'pending_approval' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <Clock className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.status.pending_approval') || 'Ждёт утверждения'}</span>
                      </span>
                    )}
                    {post.status === 'pending_rewrite' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <Clock className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.status.pending_rewrite') || 'Правка ждёт утверждения'}</span>
                      </span>
                    )}
                    {post.status === 'pending_rewrite' && rewriteKatifi?.verdict === 'pass' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <CheckCircle2 className="w-3 h-3 text-[#0B8B5E]" />
                        <span>{t('rep.gmb.katifi_badge_pass') || 'Katifi: PASSED'}</span>
                      </span>
                    )}
                    {post.status === 'pending_rewrite' && rewriteKatifi?.verdict === 'fail' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <AlertTriangle className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.status.needs_review') || 'Needs Review'}</span>
                      </span>
                    )}
                    {post.status === 'pending_rewrite' && !rewriteKatifi && rewriteKatifiFailed && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <AlertTriangle className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.katifi_badge_not_reviewed') || 'Katifi: Not Reviewed'}</span>
                      </span>
                    )}
                    {isNeedsReview && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{t('rep.gmb.status.needs_review')}</span>
                      </span>
                    )}
                    {post.status === 'expired' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#F4F4F6] text-[#687077] border border-[#E5E1D6]">
                        <Clock className="w-3 h-3 text-[#687077]" />
                        <span>{t('rep.gmb.status.expired') || 'Истёк'}</span>
                      </span>
                    )}
                    {post.status === 'rejected' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDF2F2] text-[#C93B2B] border border-[#FAD2CF]">
                        <Trash2 className="w-3 h-3 text-[#C93B2B]" />
                        <span>{t('rep.gmb.status.rejected') || 'Отклонён'}</span>
                      </span>
                    )}
                    {post.status === 'publish_unconfirmed' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FEF3C7] text-[#D97706] border border-[#FCD34D]">
                        <AlertTriangle className="w-3 h-3 text-[#D97706]" />
                        <span>{t('rep.gmb.status.publish_unconfirmed') || 'Publication Unconfirmed'}</span>
                      </span>
                    )}
                    {post.status === 'removed_by_google' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDF2F2] text-[#C93B2B] border border-[#FAD2CF]">
                        <AlertTriangle className="w-3 h-3 text-[#C93B2B]" />
                        <span>{t('rep.gmb.status.removed_by_google') || 'Removed by Google'}</span>
                      </span>
                    )}
                    {isFailed && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDF2F2] text-[#C93B2B] border border-[#FAD2CF]">
                        <span>{t('rep.gmb.status.failed')}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Warning callout for publish_unconfirmed & unresolved_claim (Round 2 B1, Round 3 B4/B5) */}
                {(post.status === 'publish_unconfirmed' || post.unresolved_claim) && (
                  <div className="p-3 rounded-xl bg-[#FFFBEB] border border-[#FCD34D] text-xs text-[#92400E] space-y-1">
                    <div className="font-bold flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-[#D97706]" />
                      <span>
                        {post.status === 'publish_unconfirmed'
                          ? (t('rep.gmb.status.publish_unconfirmed') || 'Публикация не подтверждена')
                          : (t('rep.gmb.unresolved_claim_title') || 'Мост взял пост, результата нет — проверьте карточку в Google')}
                      </span>
                    </div>
                    <p className="text-[11px] leading-relaxed">
                      {post.status === 'publish_unconfirmed'
                        ? (t('rep.gmb.publish_unconfirmed_hint') || 'Пост был отправлен в Google, но подтверждение не получено. Проверьте карточку в Google вручную, чтобы подтвердить или отменить публикацию.')
                        : (t('rep.gmb.unresolved_claim_hint') || 'Мост взял пост в работу (bridge_claimed), но отчёт не поступил. Проверьте карточку в Google: если пост вышел, подтвердите ссылку; если нет — верните в утверждённые.')}
                    </p>
                    {post.error_message && (
                      <p className="text-[10px] text-[#B45309] font-mono">{post.error_message}</p>
                    )}
                  </div>
                )}

                {/* Guard violations warning if any */}
                {post.guard_violations && post.guard_violations.length > 0 && (
                  <div className="p-3 rounded-xl bg-[#FFF8F8] border border-[#FAD2CF] text-xs text-[#C93B2B] space-y-1">
                    <div className="font-bold flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>{t('rep.gmb.status.needs_review')}</span>
                    </div>
                    <ul className="list-disc list-inside space-y-0.5 text-[11px] pl-1">
                      {post.guard_violations.map((v, vIdx) => (
                        <li key={vIdx}>{v}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Post Text */}
                <p className="text-xs text-[#20231F] leading-relaxed whitespace-pre-wrap">
                  {post.summary}
                </p>

                {/* Suggested AI rewrite preview (pending approval by staff) */}
                {post.status === 'pending_rewrite' && (() => {
                  const suggested = (post.revision_history || []).slice().reverse().find((h) => h.action === 'rewrite_suggested');
                  if (!suggested?.suggested_summary) return null;
                  return (
                    <div className="p-3 rounded-xl bg-[#FAF8F5] border border-[#E5D7B5] space-y-1.5 text-xs">
                      <div className="font-bold text-[#A97F2E] flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-[#C8A653]" />
                        <span>{t('rep.gmb.suggested_rewrite_title') || 'Предложенный вариант ИИ (ждёт утверждения):'}</span>
                      </div>
                      <p className="text-[#20231F] whitespace-pre-wrap">{suggested.suggested_summary}</p>
                      {suggested.instruction && (
                        <p className="text-[11px] text-[#687077] italic">Указание: {suggested.instruction}</p>
                      )}
                      {rewriteKatifi?.verdict === 'fail' && Array.isArray(rewriteKatifi.notes) && rewriteKatifi.notes.length > 0 && (
                        <div className="pt-2 mt-2 border-t border-[#E5D7B5] space-y-1 text-[#C93B2B]">
                          <div className="font-semibold text-[11px] flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 text-[#C93B2B]" />
                            <span>{t('rep.gmb.status.needs_review') || 'Замечания Katifi:'}</span>
                          </div>
                          <ul className="list-disc list-inside text-[11px] pl-1 space-y-0.5">
                            {rewriteKatifi.notes.map((n, nIdx) => (
                              <li key={nIdx}>{n}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* CTA URL & Live Post ID */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-[11px] text-[#687077]">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#20231F]">{post.cta_type || 'LEARN_MORE'}:</span>
                    {/^https:\/\/[^\s/?#]+/i.test(post.cta_url || '') ? (
                      <a
                        href={post.cta_url.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#C8A653] hover:underline inline-flex items-center gap-1"
                      >
                        <span className="truncate max-w-xs">{post.cta_url}</span>
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <span>—</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopyText(post.summary)}
                      className="h-6 px-2 rounded-md text-[11px] font-medium bg-[#FAF8F5] border border-[#E5E1D6] text-[#20231F] hover:bg-[#F3EFE6] inline-flex items-center gap-1 cursor-pointer transition-colors"
                      title={t('rep.gmb.btn_copy_text') || 'Скопировать текст'}
                    >
                      <Copy className="w-2.5 h-2.5 text-[#C8A653]" />
                      <span>{t('rep.gmb.btn_copy_text') || 'Скопировать текст'}</span>
                    </button>
                    {post.cta_url && (
                      <button
                        type="button"
                        onClick={() => handleCopyLink(post.cta_url)}
                        className="h-6 px-2 rounded-md text-[11px] font-medium bg-[#FAF8F5] border border-[#E5E1D6] text-[#20231F] hover:bg-[#F3EFE6] inline-flex items-center gap-1 cursor-pointer transition-colors"
                        title={t('rep.gmb.btn_copy_link') || 'Скопировать ссылку'}
                      >
                        <Copy className="w-2.5 h-2.5 text-[#C8A653]" />
                        <span>{t('rep.gmb.btn_copy_link') || 'Скопировать ссылку'}</span>
                      </button>
                    )}
                    {post.google_post_id && (
                      <span className="font-mono text-[10px] text-[#687077]">
                        Google ID: {post.google_post_id}
                      </span>
                    )}
                    {post.search_url && /^https:\/\/[^\s/?#]+/i.test(post.search_url) && (
                      <a
                        href={post.search_url.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] text-[#C8A653] hover:underline font-medium"
                      >
                        <span>{t('rep.gmb.view_in_google') || 'View in Google'}</span>
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Bottom Actions Row: Verification & API control */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#F1EEE4]">
                  {/* Verification mark badge / button */}
                  <div className="flex items-center gap-2">
                    {isVerified && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0B8B5E] bg-[#E6F6EE] px-2.5 py-1 rounded-lg border border-[#B7E5D0]">
                        <Check className="w-3.5 h-3.5 text-[#0B8B5E]" />
                        <span>
                          {t('rep.gmb.verified_at', {
                            by: post.verified_by || 'Staff',
                            date: formatNyDate(post.verified_at, lang),
                          })}
                        </span>
                      </span>
                    )}

                    {/* Katifi review button */}
                    {(post.status === 'draft' || post.status === 'needs_review' || post.status === 'pending_approval' || post.status === 'katifi_not_reviewed' || post.status === 'pending_rewrite') && (
                      <button
                        type="button"
                        onClick={() => handleVerifyWithKatifi(post.id)}
                        disabled={actionLoading[`verify_katifi_${post.id}`]}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5] hover:bg-[#F3E7C9] inline-flex items-center gap-1 transition-colors cursor-pointer"
                        title={t('rep.gmb.btn_verify_katifi') || 'Verify with Katifi'}
                      >
                        <Sparkles className={`w-3 h-3 text-[#C8A653] ${actionLoading[`verify_katifi_${post.id}`] ? 'animate-spin' : ''}`} />
                        <span>
                          {actionLoading[`verify_katifi_${post.id}`]
                            ? (t('rep.gmb.btn_verifying_katifi') || 'Katifi...')
                            : (t('rep.gmb.btn_verify_katifi') || 'Katifi')}
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Actions: Patch in Google, Remove, Approve, Mark Manual, Rewrite, Reject */}
                  <div className="flex items-center gap-2">
                    {/* Approve button: shown only after Katifi review verdict */}
                    {((post.status === 'pending_rewrite' ? Boolean(rewriteKatifi) : (post.status === 'katifi_reviewed' || isNeedsReview))) && post.status !== 'approved' && (
                      <button
                        type="button"
                        onClick={() => handleApprove(post.id, post)}
                        disabled={actionLoading[`approve_${post.id}`]}
                        className="h-7 px-3 rounded-lg text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
                      >
                        <CheckCircle2 className="w-3 h-3 text-[#C8A653]" />
                        <span>
                          {actionLoading[`approve_${post.id}`]
                            ? t('rep.gmb.btn_approving')
                            : post.status === 'pending_rewrite'
                              ? (t('rep.gmb.btn_approve_rewrite') || 'Утвердить правку')
                              : (t('rep.gmb.btn_approve') || 'Утвердить')}
                        </span>
                      </button>
                    )}

                    {/* Copy text button */}
                    {(post.status === 'pending_approval' || isNeedsReview || post.status === 'pending_rewrite') && (
                      <button
                        type="button"
                        onClick={() => handleCopyText(post.summary)}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] inline-flex items-center gap-1 transition-colors cursor-pointer shadow-2xs"
                        title={t('rep.gmb.btn_copy_text') || 'Copy text'}
                      >
                        <Copy className="w-3 h-3 text-[#687077]" />
                        <span>{t('rep.gmb.btn_copy_text') || 'Copy text'}</span>
                      </button>
                    )}

                    {/* Mark Published Manually button & waiting badge (A5):
                        Доступно для утверждённых постов (approved) без зависшего захвата */}
                    {post.status === 'approved' && !post.unresolved_claim && (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#2980B9] font-medium bg-[#EBF5FB] px-2 py-1 rounded-md border border-[#AED6F1]">
                          <Clock className="w-3 h-3 text-[#2980B9]" />
                          <span>{t('rep.gmb.waiting_for_autopublish') || 'Waiting for auto-publish'}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleOpenManualModal(post)}
                          disabled={actionLoading[`manual_${post.id}`]}
                          className="h-7 px-3 rounded-lg text-xs font-semibold bg-[#2980B9] text-white hover:bg-[#2471A3] inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
                        >
                          <Check className="w-3 h-3" />
                          <span>{t('rep.gmb.btn_mark_manual') || 'Опубликовано вручную'}</span>
                        </button>
                      </div>
                    )}

                    {/* Publication Unconfirmed & Unresolved Claim (Round 2 B1, Round 3 B4/B5):
                        Сбой после нажатия Post или зависший захват моста.
                        Две кнопки владельца:
                        1. Подтвердить: опубликован (ссылка Google)
                        2. Не опубликован (вернуть в утверждённые / снять захват) */}
                    {(post.status === 'publish_unconfirmed' || post.unresolved_claim) && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#D97706] font-medium bg-[#FEF3C7] px-2 py-1 rounded-md border border-[#FCD34D]">
                          <AlertTriangle className="w-3 h-3 text-[#D97706]" />
                          <span>
                            {post.status === 'publish_unconfirmed'
                              ? (t('rep.gmb.status.publish_unconfirmed') || 'Publication Unconfirmed')
                              : (t('rep.gmb.reason_unresolved_claim') || 'Unresolved Bridge Claim')}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleOpenConfirmPubModal(post)}
                          disabled={confirmingPub}
                          className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-[#2E7D32] text-white hover:bg-[#256628] inline-flex items-center gap-1 transition-colors cursor-pointer shadow-xs"
                          title={t('rep.gmb.btn_confirm_published') || 'Confirm Published'}
                        >
                          <Check className="w-3 h-3" />
                          <span>{t('rep.gmb.btn_confirm_published') || 'Подтвердить: опубликован (ссылка)'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenRevertAppModal(post)}
                          disabled={revertingApp}
                          className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] inline-flex items-center gap-1 transition-colors cursor-pointer shadow-2xs"
                          title={post.status === 'rejected' ? (t('rep.gmb.btn_clear_claim') || 'Clear Claim') : (t('rep.gmb.btn_revert_to_approved') || 'Not Published')}
                        >
                          <X className="w-3 h-3 text-[#687077]" />
                          <span>
                            {post.status === 'rejected'
                              ? (t('rep.gmb.btn_clear_claim') || 'Не выходил (снять захват)')
                              : (t('rep.gmb.btn_revert_to_approved') || 'Не опубликован')}
                          </span>
                        </button>
                      </div>
                    )}

                    {((['publish_unconfirmed', 'published', 'published_manually'].includes(post.status) || post.unresolved_claim) && post.status !== 'removed_by_google') && (
                      <button
                        type="button"
                        data-testid={`google-policy-removed-btn-${post.id}`}
                        onClick={() => handleOpenGooglePolicyModal(post)}
                        disabled={submittingGooglePolicy}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-[#FDF2F2] border border-[#FAD2CF] text-[#C0392B] hover:bg-[#FCEBEA] inline-flex items-center gap-1 transition-colors cursor-pointer shadow-2xs"
                        title={t('rep.gmb.btn_google_policy_removed') || 'Google Policy Removal (Violation)'}
                      >
                        <AlertTriangle className="w-3 h-3 text-[#C0392B]" />
                        <span>{t('rep.gmb.btn_google_policy_removed') || 'Google Policy Removal (Violation)'}</span>
                      </button>
                    )}

                    {isLive && (
                      <button
                        type="button"
                        onClick={() => handleOpenRewrite(post)}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-[#FAF4E5] border border-[#E5D7B5] text-[#A97F2E] hover:bg-[#F3E7C9] inline-flex items-center gap-1 transition-colors cursor-pointer"
                        title={t('rep.gmb.btn_rewrite')}
                      >
                        <RefreshCw className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.btn_rewrite')}</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOpenPatch(post)}
                      className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] inline-flex items-center gap-1 transition-colors cursor-pointer"
                      title={t('rep.gmb.btn_patch')}
                    >
                      <Edit3 className="w-3 h-3 text-[#687077]" />
                      <span>{t('rep.gmb.btn_patch')}</span>
                    </button>

                    {(post.status === 'pending_approval' || isNeedsReview || post.status === 'pending_rewrite') && (
                      <button
                        type="button"
                        onClick={() => handleOpenReject(post)}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#FAD2CF] text-[#C93B2B] hover:bg-[#FDF2F2] inline-flex items-center gap-1 transition-colors cursor-pointer"
                        title={t('rep.gmb.btn_reject') || 'Отклонить'}
                      >
                        <Trash2 className="w-3 h-3 text-[#C93B2B]" />
                        <span>{t('rep.gmb.btn_reject') || 'Отклонить'}</span>
                      </button>
                    )}

                    {post.status !== 'pending_approval' && !isNeedsReview && post.status !== 'pending_rewrite' && (
                      <button
                        type="button"
                        onClick={() => handleDelete(post)}
                        disabled={actionLoading[`delete_${post.id}`]}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#FAD2CF] text-[#C93B2B] hover:bg-[#FDF2F2] inline-flex items-center gap-1 transition-colors cursor-pointer"
                        title={t('rep.gmb.btn_delete')}
                      >
                        <Trash2 className="w-3 h-3 text-[#C93B2B]" />
                        <span>{t('rep.gmb.btn_delete')}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── CREATE / DRAFT FORM (CANON TOPICS & AUTO-FILL URL) ───────────────── */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-[#F1EEE4]">
          <h3 className="text-sm font-serif font-bold text-[#20231F] flex items-center gap-2">
            <Send className="w-4 h-4 text-[#C8A653]" />
            <span>{t('rep.gmb.create_tab_title')}</span>
          </h3>
          <button
            type="button"
            onClick={handleGenerateAI}
            disabled={generating || !geminiConfigured}
            title={!geminiConfigured ? (t('rep.gmb.gemini_key_missing') || 'GEMINI_API_KEY is not configured') : ''}
            className="h-8 px-3 rounded-lg text-xs font-semibold bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5] hover:bg-[#F3E7C9] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Sparkles className={`w-3.5 h-3.5 text-[#C8A653] ${generating ? 'animate-spin' : ''}`} />
            <span>{generating ? t('rep.modal.gmb.posts.btn_generating') : t('rep.modal.gmb.posts.btn_generate')}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Office selector */}
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.gmb.select_office_label')}
            </label>
            {officesError ? (
              <div className="p-2 rounded-xl border border-red-200 bg-red-50 text-[11px] text-red-800">
                {officesError}
              </div>
            ) : locations.length === 0 ? (
              <div className="p-2 rounded-xl border border-amber-200 bg-amber-50 text-[11px] text-amber-800">
                <span>{t('rep.gmb.no_locations_hint') || 'Create the office profile first'} </span>
                <a
                  href="/reputation?tab=locations"
                  onClick={(e) => {
                    const url = new URL(window.location);
                    url.searchParams.set('tab', 'locations');
                    window.history.pushState({}, '', url);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="font-semibold underline hover:text-amber-900 cursor-pointer"
                >
                  {t('rep.gmb.link_office_profiles') || 'Office Profiles'}
                </a>
              </div>
            ) : (
              <select
                value={targetOffice}
                onChange={(e) => setTargetOffice(e.target.value)}
                className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              >
                <option value="">{t('rep.gmb.select_office_placeholder') || '— Select Office —'}</option>
                {locations.map((loc) => (
                  <option key={loc.id || loc.office_key} value={loc.office_key || loc.id}>
                    {loc.title ? `${loc.title} (${loc.office_key})` : (loc.office_key || loc.address_line)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Language selector */}
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.gmb.select_language_label')}
            </label>
            <div className="h-9 px-3 flex items-center rounded-xl border border-[#E5E1D6] bg-[#FAF8F5] text-xs font-semibold text-[#20231F]">
              {t('rep.gmb.language_en')} (English Only)
            </div>
          </div>

          {/* Canonical topic selector */}
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.gmb.select_topic_label')}
            </label>
            <select
              value={selectedTopicObj?.slug || selectedTopicObj?.id || selectedTopicObj?.name || ''}
              onChange={(e) => handleTopicChange(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            >
              {canonicalTopics.map((topic) => {
                const topicKey = topic.slug || topic.id || topic.name;
                const topicLabel = postLanguage === 'ru' ? (topic.title_ru || topic.name) : (topic.title_en || topic.name);
                return (
                  <option key={topicKey} value={topicKey}>
                    {topicLabel}
                  </option>
                );
              })}
            </select>
            {topicsError && (
              <div className="p-2 rounded-xl border border-red-200 bg-red-50 text-[11px] text-red-800 mt-1">
                {topicsError}
              </div>
            )}
          </div>
        </div>

        {/* Text area */}
        <div>
          <label className="block text-xs font-semibold text-[#20231F] mb-1">
            {t('rep.modal.gmb.posts.content_label')}
          </label>
          <textarea
            rows={4}
            value={postSummary}
            onChange={(e) => setPostSummary(e.target.value)}
            placeholder={t('rep.modal.gmb.posts.content_placeholder')}
            className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
          />
        </div>

        {/* CTA URL destination */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.modal.gmb.posts.cta_button_label')}
            </label>
            <select
              value={postCtaType}
              onChange={(e) => setPostCtaType(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            >
              <option value="LEARN_MORE">{t('rep.modal.gmb.posts.cta_learn_more')}</option>
              <option value="BOOK">{t('rep.modal.gmb.posts.cta_book')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.modal.gmb.posts.cta_url_label')}
            </label>
            <input
              type="text"
              value={postCtaUrl}
              onChange={(e) => setPostCtaUrl(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
          <div>
            {!targetOffice && locations.length > 0 && (
              <span className="text-xs text-[#DC2626] font-medium">
                {t('rep.gmb.select_office_hint')}
              </span>
            )}
            {!postsApiEnabled && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5 max-w-xl">
                {t('rep.gmb.posts_api_disabled_reason') || 'Google has not opened the Posts API for this project. Save the draft for Katifi review and owner approval; after approval, publish manually in Google.'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyText}
              disabled={!postSummary.trim()}
              className="h-9 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Copy className="w-3.5 h-3.5 text-[#687077]" />
              <span>{t('rep.gmb.btn_copy_text') || 'Copy text'}</span>
            </button>
            <button
              type="button"
              onClick={handleCreatePost}
              disabled={submitting || !postSummary.trim() || !targetOffice}
              className="h-9 px-5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Send className="w-3.5 h-3.5 text-[#C8A653]" />
              <span>{submitting ? (t('rep.gmb.btn_saving_draft') || 'Saving draft...') : (t('rep.gmb.btn_save_draft') || 'Save draft')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── PATCH POST MODAL (EDIT IN GOOGLE) ─────────────────────────────────── */}
      {editingPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-[#C8A653]" />
              <span>{t('rep.gmb.patch_title')}</span>
            </h3>

            {editingPost.status === 'published_manually' && (
              <div data-testid="manual-edit-warning" className="p-3 rounded-xl bg-[#FFF8E1] border border-[#FFE082] text-xs text-[#795548] leading-relaxed">
                {t('rep.gmb.manual_edit_warning') || 'Пост опубликован вручную — измените его в Google сами; OS изменит только свою запись.'}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.patch_summary_label')}
              </label>
              <textarea
                rows={5}
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingPost(null)}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel')}
              </button>
              <button
                type="button"
                onClick={handleSavePatch}
                disabled={patching || !editSummary.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Check className="w-3.5 h-3.5 text-[#C8A653]" />
                <span>{patching ? t('rep.gmb.patch_saving') : t('rep.gmb.patch_save')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REWRITE POST MODAL (A2) ────────────────────────────────────────── */}
      {rewritingPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-[#C8A653]" />
              <span>{t('rep.gmb.rewrite_title')}</span>
            </h3>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.rewrite_feedback_label')}
              </label>
              <textarea
                rows={4}
                value={rewriteFeedback}
                onChange={(e) => setRewriteFeedback(e.target.value)}
                placeholder={t('rep.gmb.rewrite_feedback_placeholder')}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRewritingPost(null)}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel')}
              </button>
              <button
                type="button"
                onClick={handleRewrite}
                disabled={rewriting}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Sparkles className={`w-3.5 h-3.5 text-[#C8A653] ${rewriting ? 'animate-spin' : ''}`} />
                <span>{rewriting ? t('rep.gmb.rewrite_submitting') : t('rep.gmb.btn_rewrite')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT POST MODAL ──────────────────────────────────────────────── */}
      {rejectingPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-[#C93B2B]" />
              <span>{t('rep.gmb.reject_title') || 'Отклонить черновик поста'}</span>
            </h3>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.reject_reason_label') || 'Причина отклонения'}
              </label>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('rep.gmb.reject_reason_placeholder') || 'Укажите причину отклонения...'}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRejectingPost(null)}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel')}
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={rejecting}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#C93B2B] text-white hover:bg-[#A82B1D] disabled:opacity-50 inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Trash2 className="w-3.5 h-3.5 text-white" />
                <span>{rejecting ? (t('rep.state.loading') || 'Отклонение...') : (t('rep.gmb.reject_submit') || 'Отклонить черновик')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MARK PUBLISHED MANUALLY MODAL ──────────────────────────────────── */}
      {manualModalPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
                <Check className="w-4 h-4 text-[#2980B9]" />
                <span>{t('rep.gmb.manual_modal_title') || 'Отметить как опубликованное вручную'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setManualModalPost(null)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {t('rep.gmb.manual_modal_desc') || 'Укажите ссылку на опубликованный пост в Google и подтвердите публикацию.'}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.manual_modal_url_label') || 'Ссылка на пост в Google'}
              </label>
              <input
                type="url"
                value={manualGoogleUrl}
                onChange={(e) => setManualGoogleUrl(e.target.value)}
                placeholder={t('rep.gmb.manual_modal_url_placeholder') || 'https://posts.gle/... или https://maps.app.goo.gl/...'}
                className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#2980B9]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setManualModalPost(null)}
                disabled={markingManual}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.manual_modal_btn_cancel') || 'Отмена'}
              </button>
              <button
                type="button"
                onClick={handleConfirmManual}
                disabled={markingManual}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#2980B9] text-white hover:bg-[#2471A3] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {markingManual && <RefreshCw className="w-3 h-3 animate-spin" />}
                <span>{t('rep.gmb.manual_modal_btn_confirm') || 'Подтвердить публикацию'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── OWNER OVERRIDE APPROVAL MODAL (KATIFI REMARKS) ──────────────────── */}
      {overrideModalPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#A97F2E]" />
                <span>{t('rep.gmb.katifi_override_title') || 'Approve Despite Katifi Remarks'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setOverrideModalPost(null)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {t('rep.gmb.katifi_override_desc') || 'Katifi identified compliance remarks. As firm owner, enter a mandatory comment explaining your approval override:'}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.katifi_override_comment_label') || 'Owner Override Comment'}
              </label>
              <textarea
                rows={3}
                value={overrideComment}
                onChange={(e) => setOverrideComment(e.target.value)}
                placeholder="Reason for approving despite Katifi remarks..."
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setOverrideModalPost(null)}
                disabled={approvingOverride}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirmOverride}
                disabled={approvingOverride || !overrideComment.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {approvingOverride && <RefreshCw className="w-3 h-3 animate-spin text-[#C8A653]" />}
                <span>{t('rep.gmb.katifi_override_btn') || 'Approve with Override'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RESUME AUTOPUBLISH MODAL (A4) ─────────────────────────────────── */}
      {resumeModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#C0392B]" />
                <span>{t('rep.gmb.resume_modal_title') || 'Resume Auto-Publish'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setResumeModalOpen(false)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {t('rep.gmb.resume_modal_desc') || 'The auto-publish pipeline was stopped. As the firm owner, please provide a mandatory explanation to clear the stop flag and resume automatic publishing to Google.'}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.resume_comment_label') || 'Owner explanation (mandatory)'}
              </label>
              <textarea
                rows={3}
                value={resumeComment}
                onChange={(e) => setResumeComment(e.target.value)}
                placeholder={t('rep.gmb.resume_comment_placeholder') || 'Reason for resuming auto-publish...'}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setResumeModalOpen(false)}
                disabled={resumingAutopublish}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleResumeAutopublish}
                disabled={resumingAutopublish || !resumeComment.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#C0392B] text-white hover:bg-[#A93226] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {resumingAutopublish && <RefreshCw className="w-3 h-3 animate-spin text-white" />}
                <span>{t('rep.gmb.btn_confirm_resume') || 'Resume Pipeline'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM PUBLISHED MODAL (Round 2, B1) ─────────────────────────── */}
      {confirmPubPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
                <Check className="w-4 h-4 text-[#2E7D32]" />
                <span>{t('rep.gmb.confirm_pub_modal_title') || 'Confirm Publication in Google'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setConfirmPubPost(null)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {t('rep.gmb.confirm_pub_modal_desc') || 'If you checked the Google Business Profile card and the post was indeed published, enter the Google Post URL and your verification comment.'}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.confirm_pub_url_label') || 'Google Post URL (mandatory)'}
              </label>
              <input
                type="text"
                value={confirmPubUrl}
                onChange={(e) => setConfirmPubUrl(e.target.value)}
                placeholder="https://posts.gle/... or https://maps.app.goo.gl/..."
                className="w-full p-2.5 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.confirm_pub_comment_label') || 'Owner explanation comment (mandatory)'}
              </label>
              <textarea
                rows={3}
                value={confirmPubComment}
                onChange={(e) => setConfirmPubComment(e.target.value)}
                placeholder={t('rep.gmb.confirm_pub_comment_placeholder') || 'e.g. Verified live post on Google Business Profile card manually...'}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setConfirmPubPost(null)}
                disabled={confirmingPub}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirmPublished}
                disabled={confirmingPub || !confirmPubUrl.trim() || !confirmPubComment.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#2E7D32] text-white hover:bg-[#256628] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {confirmingPub && <RefreshCw className="w-3 h-3 animate-spin text-white" />}
                <span>{t('rep.gmb.btn_confirm_published') || 'Confirm Published'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REVERT TO APPROVED MODAL (Round 2, B1) ─────────────────────────── */}
      {revertAppPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#D97706]" />
                <span>
                  {revertAppPost?.status === 'rejected'
                    ? (t('rep.gmb.clear_claim_modal_title') || 'Clear Bridge Claim')
                    : (t('rep.gmb.revert_app_modal_title') || 'Return to Approved (Not Published)')}
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setRevertAppPost(null)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {revertAppPost?.status === 'rejected'
                ? (t('rep.gmb.clear_claim_modal_desc') || 'Confirm that this post did not publish on Google Business Profile. This will clear the bridge lock without approving the post.')
                : (t('rep.gmb.revert_app_modal_desc') || 'If the post did not appear on Google Business Profile, confirm returning it to approved status. Please provide an owner explanation comment.')}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.revert_app_comment_label') || 'Owner explanation comment (mandatory)'}
              </label>
              <textarea
                rows={3}
                value={revertAppComment}
                onChange={(e) => setRevertAppComment(e.target.value)}
                placeholder={t('rep.gmb.revert_app_comment_placeholder') || 'e.g. Checked Google Business Profile card, post was not created...'}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setRevertAppPost(null)}
                disabled={revertingApp}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleRevertToApproved}
                disabled={revertingApp || !revertAppComment.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {revertingApp && <RefreshCw className="w-3 h-3 animate-spin text-[#C8A653]" />}
                <span>
                  {revertAppPost?.status === 'rejected'
                    ? (t('rep.gmb.btn_clear_claim') || 'Not Published (Clear Claim)')
                    : (t('rep.gmb.btn_revert_to_approved') || 'Return to Approved')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── GOOGLE POLICY REMOVED MODAL (Task 30.10.2026, B1) ───────────────── */}
      {googlePolicyPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#C0392B] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#C0392B]" />
                <span>{t('rep.gmb.google_policy_modal_title') || 'Google снял пост (нарушение правил)'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setGooglePolicyPost(null)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {t('rep.gmb.google_policy_modal_desc') || 'Record that Google removed this post due to content policy violation and disabled posting on this profile. This sets the pipeline stop flag until Google restores posting.'}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.google_notice_label') || 'Google Notice Text / Subject (mandatory)'}
              </label>
              <textarea
                rows={3}
                data-testid="google-notice-input"
                value={googlePolicyNotice}
                onChange={(e) => setGooglePolicyNotice(e.target.value)}
                placeholder={t('rep.gmb.google_notice_placeholder') || "e.g. Estate Law New York, your post violates Google's content policy (Routing ID: DPNB)..."}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C0392B]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.google_policy_comment_label') || 'Owner comment (mandatory)'}
              </label>
              <textarea
                rows={3}
                data-testid="google-policy-comment-input"
                value={googlePolicyComment}
                onChange={(e) => setGooglePolicyComment(e.target.value)}
                placeholder={t('rep.gmb.google_policy_comment_placeholder') || 'Explain the action taken or appeal plan...'}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C0392B]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setGooglePolicyPost(null)}
                disabled={submittingGooglePolicy}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                data-testid="confirm-google-policy-removed-btn"
                onClick={handleConfirmGooglePolicyRemoved}
                disabled={submittingGooglePolicy || !googlePolicyNotice.trim() || !googlePolicyComment.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#C0392B] text-white hover:bg-[#A93226] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {submittingGooglePolicy && <RefreshCw className="w-3 h-3 animate-spin text-white" />}
                <span>{t('rep.gmb.btn_confirm_google_policy_removed') || 'Confirm Removal by Google'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GOOGLE POSTING RESTORED MODAL (Task 30.10.2026, B1) ───────────────── */}
      {restoreGoogleModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#F1EEE4]">
              <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
                <Check className="w-4 h-4 text-[#2E7D32]" />
                <span>{t('rep.gmb.restore_google_modal_title') || 'Confirm Google Restored Posting'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setRestoreGoogleModalOpen(false)}
                className="text-[#687077] hover:text-[#20231F] text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#687077] leading-relaxed">
              {t('rep.gmb.restore_google_modal_desc') || 'Only proceed if Google support or appeal confirmed that posting on this Business Profile has been re-enabled.'}
            </p>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.restore_google_comment_label') || 'Confirmation details and date (mandatory)'}
              </label>
              <textarea
                rows={3}
                data-testid="restore-google-comment-input"
                value={restoreGoogleComment}
                onChange={(e) => setRestoreGoogleComment(e.target.value)}
                placeholder={t('rep.gmb.restore_google_comment_placeholder') || 'e.g. Appeal granted on 11/02/2026, confirmed posting functionality active in Google Business Profile...'}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#F1EEE4]">
              <button
                type="button"
                onClick={() => setRestoreGoogleModalOpen(false)}
                disabled={restoringGooglePosting}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#687077] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                data-testid="confirm-restore-google-btn"
                onClick={handleRestoreGooglePosting}
                disabled={restoringGooglePosting || !restoreGoogleComment.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#2E7D32] text-white hover:bg-[#256628] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
              >
                {restoringGooglePosting && <RefreshCw className="w-3 h-3 animate-spin text-white" />}
                <span>{t('rep.gmb.btn_confirm_restore_google_posting') || 'Restore Posting'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GmbPostsTab;
